import dayjs from 'dayjs';

export type ValueGetterOptions = {
  key: string;
  format?: 'unitId' | 'date';
  /** using .map overwrites default handlers */
  map?: (value: any) => string;
};
const dateFormat = 'YYYY-MM-DD';

abstract class Handler<T> {
  abstract canHandle(value: unknown, options?: ValueGetterOptions): boolean;
  abstract handle(value: T, options?: ValueGetterOptions): string;
}

class NothingHandler extends Handler<null | undefined> {
  canHandle(value: unknown): boolean {
    return value === undefined || value === null;
  }

  handle(): string {
    return '';
  }
}

class DateHandler extends Handler<string> {
  canHandle(value: unknown, options?: ValueGetterOptions): boolean {
    return options?.format === 'date' && dayjs(`${value}`).isValid();
  }

  handle(value: string): string {
    return dayjs(value).format(dateFormat);
  }
}

type AddressField = {
  addressLine: string;
  city: string;
  state: string;
  postalCode: string;
  country: {
    name: string;
    code: string;
  };
};
/**
 * Implementation for that handler should be inlined with definition of `address` field in unit.
 * Generic approach to displaying values gives me slight cancer though. Hopefully we'll make SAFc use more dedicated code.
 *
 * ~ Mateusz Koteja, 06.10.2025
 */
export class AddressHandler extends Handler<AddressField> {
  canHandle(rawValue: unknown): boolean {
    if (typeof rawValue !== 'object' || rawValue === null || Array.isArray(rawValue)) {
      return false;
    }

    const value = rawValue as Record<string, unknown>;

    return 'addressLine' in value
      && 'city' in value
      && 'state' in value
      && 'postalCode' in value
      && 'country' in value;
  }

  handle(value: AddressField): string {
    return `${value.addressLine}, ${value.city}, ${value.state}, ${value.postalCode}, ${value.country.name}`;
  }
}

class UnitIdHandler extends Handler<string> {
  canHandle(_: unknown, options?: ValueGetterOptions): boolean {
    return options?.format === 'unitId';
  }

  handle(value: string): string {
    return value.slice(0, 8);
  }
}

class StringHandler extends Handler<string> {
  canHandle(value: unknown): boolean {
    return typeof value === 'string';
  }

  handle(value: string): string {
    return value;
  }
}

class NumberHandler extends Handler<number> {
  canHandle(value: unknown): boolean {
    return !Number.isNaN(Number(value));
  }

  handle(value: number): string {
    return value.toString();
  }
}

class BooleanHandler extends Handler<boolean> {
  canHandle(value: unknown): boolean {
    return typeof value === 'boolean';
  }

  handle(value: boolean): string {
    if (value) {
      return 'Yes';
    }
    return 'No';
  }
}

class ArrayHandler extends Handler<Array<unknown>> {
  canHandle(value: unknown): boolean {
    return Array.isArray(value);
  }

  handle(value: Array<unknown>, options?: ValueGetterOptions): string {
    return value.map(value => handleValue(value, options)).join(', ');
  }
}

class ObjectHandler extends Handler<Record<string, unknown>> {
  canHandle(value: unknown): boolean {
    return typeof value === 'object';
  }

  handle(value: Record<string, unknown>, options?: ValueGetterOptions): string {
    return Object.values(value)
      .map(value => handleValue(value, options))
      .join(', ');
  }
}

// due to sheer number of times it is executed try to keep more complex handlers at the end of the list
// remember that order of handlers is important for logic execution
const handlers: Handler<any>[] = [
  new NothingHandler(),
  new DateHandler(),
  new UnitIdHandler(),
  new BooleanHandler(),
  new NumberHandler(),
  new StringHandler(),
  new ArrayHandler(),
  new AddressHandler(),
  new ObjectHandler(),
];

export const handleValue = (value: unknown, options?: ValueGetterOptions) => {
  const handler = handlers.find(handler => handler.canHandle(value, options));

  if (!handler) {
    console.error(`Cannot handle value: ${value}`);
    return '';
  }

  if (options?.map && value !== undefined) {
    return options.map(value);
  }

  const handledValue = handler.handle(value, options);

  return handledValue;
};

export const valueToString = (
  rawValue: unknown,
  options?: ValueGetterOptions,
) => {
  return handleValue(rawValue, options);
};
